// CreateCollectionTasks.jsx
import React, { useEffect, useState, useContext } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, Link as RouterLink } from "react-router-dom";

import {
  searchPatient,
  useFetchPatient,
  FetchPatient,
  createPatient,
} from "../../api/collection";
import { toast } from "sonner";
import { AuthContext } from "../../context/AuthContextProvider";
import {
  ANALYTICS_EVENTS,
  logAnalyticsEvent,
  PATIENT_TYPE,
  CREATE_PROFILE_METHOD,
  PATIENT_DETAILS_FETCH_METHOD,
  COLLECTION_SOURCE,
  NOT_SET,
} from "../../analytics";
import CentreSelect from "../../components/CentreSelect";
import CountryCodePicker from "../../components/CountryCodePicker";
import OtpInput from "react-otp-input";
import UserFlow from "./UserFlow";
import Timer from "otp-timer";
import AbhaVerificationModal from "../../components/AbhaVerificationModal";
import {
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Typography,
  Box,
  Chip,
  Dialog,
  Breadcrumbs,
  Link,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { LoadingButton } from "@mui/lab";
import {
  getPhoneNumberLimit,
  getPhoneNumberLimitMessage,
  sanitizePhoneNumber,
} from "../../utils/phoneNumber";

// ---- helper to normalise patient API -> isUser shape ----
const mapPatientToIsUser = (p) => {
  if (!p) {
    return {
      first_name: "",
      middle_name: "",
      last_name: "",
      uh_id: "",
      dateOfBirth: "",
      ref_no: "",
      email: "",
      abha_number: "",
      adhaar_number: "",
      phone: "",
      countryCode: "+91",
      age: "",
      gender: "",
      id: 0,
      isCreate: true,
      isAbhaVerified: false,
      isVerifiedUser: false,
      abhaDetails: null,
      label: "Create New Patient",
      value: 0,
      patientId: "",
    };
  }

  return {
    first_name: p?.firstName || "",
    middle_name: p?.middleName || "",
    last_name: p?.lastName || "",
    uh_id: p?.patientId || "",
    dateOfBirth: p?.dateOfBirth || p?.abhaDetails?.dateOfBirth || "",
    ref_no: p?.secondaryReferenceNumber || "",
    email: p?.userEmail || p?.email || "",
    adhaar_number: p?.aadhaarNumber || "",
    // prefer nested abhaDetails.abhaNumber, fall back to top-level if present
    abha_number: p?.abhaDetails?.abhaNumber || p?.abhaNumber || "",
    phone: p?.phoneNo || p?.phoneNumber || "",
    countryCode: p?.countryCode || p?.country_code || "+91",
    age: p?.age || "",
    gender: p?.gender || "",
    id: p?._id || 0,
    isCreate: false,
    isAbhaVerified: !!p?.isAbhaVerified,
    isVerifiedUser: !!p?.isVerifiedUser,
    abhaDetails: p?.abhaDetails || null,
    label: "Create New Patient",
    value: p?._id || 0,
    patientId: p?.patientId || "",
  };
};

// heuristic classification of the search box's free-text query for analytics only
const resolveSearchedBy = (query) =>
  /^\d+$/.test(query) ? "phone_number" : "uhid_or_reference";

function CreateCollectionTasks() {
  const navigate = useNavigate();
  const { user: loggedInUser } = useContext(AuthContext);

  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  const [isUser, setIsUser] = useState(mapPatientToIsUser(null));
  const [args, setArgs] = useState({});
  const { user, isLoading: patientLoading } = useFetchPatient(args);

  const [selectedCentre, setSelectedCentre] = useState(null);
  const [sendotp, setSendOtp] = useState(false);
  const [otp, setOtp] = useState("");
  const [verifyOtpLoading, setVerifyOtpLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [showAbha, setShowAbha] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [patientSource, setPatientSource] = useState(COLLECTION_SOURCE.GUEST);
  const phoneLimit = getPhoneNumberLimit(isUser.countryCode || "+91");
  const getPatientPhonePayload = () => ({
    phoneNumber: `${isUser.countryCode || "+91"}${isUser.phone || ""}`,
    phoneNo: isUser.phone || "",
    countryCode: isUser.countryCode || "+91",
  });

  // when fetched user changes (from OTP or direct fetch)
  useEffect(() => {
    if (user) {
      setIsUser(mapPatientToIsUser(user));
    }
  }, [user]);

  // centre selection
  const handleCentreChange = (selectedOption, meta = {}) => {
    setSelectedCentre(selectedOption);
    logAnalyticsEvent(ANALYTICS_EVENTS.CENTRE_SELECTED, {
      auto_selected: !!meta.auto,
      abha_enabled: !!selectedOption?.isAbhaVerified,
    });
    setStep(1);
  };
  // search patients
  const handleSearch = async (query) => {
    const trimmedQuery = (query || "").trim();

    logAnalyticsEvent(ANALYTICS_EVENTS.SEARCH_BUTTON_CLICKED, {
      searched_by: trimmedQuery ? resolveSearchedBy(trimmedQuery) : NOT_SET,
      has_query: !!trimmedQuery,
      centre_selected: !!selectedCentre,
    });

    if (!selectedCentre) {
      toast.error("Please select a centre first");
      setHasSearched(false);
      return;
    }
    if (!trimmedQuery) {
      setSearchError(true);
      toast.error("Please enter a phone, UHID, or Collection ID");
      setHasSearched(false);
      return;
    }

    setSearchQuery(trimmedQuery);

    const searchErrorParams = {
      userId: loggedInUser?.virtualId,
      _id: loggedInUser?._id,
      phone_number: /^\d+$/.test(trimmedQuery) ? trimmedQuery : "",
      business_id: loggedInUser?.businessId,
      search_key: trimmedQuery,
    };

    try {
      setVerifyOtpLoading(true);
      const response = await searchPatient({ searchText: trimmedQuery });

      if (response.success) {
        const results = response.data || [];
        setFilteredData(results);
        logAnalyticsEvent(ANALYTICS_EVENTS.PATIENT_SELECTED, {
          searched_by: resolveSearchedBy(trimmedQuery),
          verified_patient: results.some((p) => p?.isVerifiedUser),
          abha_verified: results.some((p) => p?.isAbhaVerified),
        });

        if (results.length > 0) {
          logAnalyticsEvent(ANALYTICS_EVENTS.PATIENT_SEARCH_FOUND, {
            searched_by: resolveSearchedBy(trimmedQuery),
            result_count: results.length,
            verified_patient: results.some((p) => p?.isVerifiedUser),
            abha_verified: results.some((p) => p?.isAbhaVerified),
          });
        } else {
          logAnalyticsEvent(ANALYTICS_EVENTS.PATIENT_SEARCH_EMPTY, {
            searched_by: resolveSearchedBy(trimmedQuery),
            search_key: trimmedQuery,
          });
        }
      } else {
        setFilteredData([]);
        logAnalyticsEvent(ANALYTICS_EVENTS.PATIENT_SEARCH_ERROR, {
          ...searchErrorParams,
          error: response.message,
        });
      }
    } catch (error) {
      console.error("Error searching patients:", error);
      setFilteredData([]);
      logAnalyticsEvent(ANALYTICS_EVENTS.PATIENT_SEARCH_ERROR, {
        ...searchErrorParams,
        error: error.message,
      });
    } finally {
      setVerifyOtpLoading(false);
      setHasSearched(true);
    }
  };

  // select patient from list OR create new
  // const handleSelectUser = async (option) => {
  //   if (option === null) {
  //     setIsUser(mapPatientToIsUser(null));
  //     setStep(3);
  //     return;
  //   }

  //   // option comes from searchPatient -> same shape as sample JSON
  //   const baseUser = mapPatientToIsUser(option);
  //   setIsUser(baseUser);

  //   if (option?.isVerifiedUser) {
  //     // already verified -> directly fetch full details
  //     setStep(3);
  //     try {
  //       setVerifyOtpLoading(true);
  //       const response = await FetchPatient({ id: option._id, otp: "", withoutConsent: true });
  //       if (response.success) {
  //         setIsUser(mapPatientToIsUser(response.data));
  //       } else {
  //         toast.error(response.message);
  //       }
  //     } catch (error) {
  //       console.error("Error fetching verified user:", error);
  //       toast.error(error.message);
  //     } finally {
  //       setVerifyOtpLoading(false);
  //     }
  //   } else {
  //     setStep(2);
  //   }
  // };

  const handleSelectUser = async (option) => {
    if (option === null) {
      // New patient → skip OTP → go directly
      setIsUser(mapPatientToIsUser(null));
      setPatientSource(COLLECTION_SOURCE.GUEST);
      logAnalyticsEvent(ANALYTICS_EVENTS.CREATE_NEW_PROFILE, {
        method: CREATE_PROFILE_METHOD.GUEST,
      });
      setStep(3);
      return;
    }
    // if (option === null) {
    //   try {
    //     setVerifyOtpLoading(true);

    //     const response = await createPatient({
    //       phoneNumber: isUser.phone,
    //       withoutConsent: true,
    //     });

    //     if (response.success) {
    //       setIsUser(mapPatientToIsUser(response.data));
    //       setStep(3);
    //     } else {
    //       toast.error(response.message);
    //     }
    //   } catch (error) {
    //     console.error("Error creating patient:", error);
    //     toast.error(error.message);
    //   } finally {
    //     setVerifyOtpLoading(false);
    //   }

    //   return;
    // }

    const baseUser = mapPatientToIsUser(option);
    setIsUser(baseUser);

    try {
      setVerifyOtpLoading(true);
      const response = await FetchPatient({
        id: option._id,
        withoutConsent: true,
      });

      if (response.success) {
        const fetchedUser = mapPatientToIsUser(response.data);
        setIsUser(fetchedUser);
        setPatientSource(COLLECTION_SOURCE.VERIFIED);
        logAnalyticsEvent(ANALYTICS_EVENTS.PATIENT_DETAILS_FETCHED, {
          method: PATIENT_DETAILS_FETCH_METHOD.ALREADY_VERIFIED,
          abha_verified: !!fetchedUser.isAbhaVerified,
        });
        setStep(3);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Error fetching patient:", error);
      toast.error(error.message);
    } finally {
      setVerifyOtpLoading(false);
    }
  };


  // OTP verification
  const handleOtpVerification = async (otpVal) => {
    const patientType = isUser.isCreate ? PATIENT_TYPE.NEW : PATIENT_TYPE.EXISTING;
    try {
      setVerifyOtpLoading(true);
      let response;

      if (isUser.isCreate) {
        response = await createPatient({
          ...getPatientPhonePayload(),
          otp: otpVal,
        });
      } else {
        response = await FetchPatient({
          id: isUser.id,
          otp: otpVal,
        });
      }

      if (response.success) {
        toast.success(response.message);
        logAnalyticsEvent(ANALYTICS_EVENTS.OTP_VERIFIED, {
          patient_type: patientType,
        });

        if (!isUser.isCreate && response.data) {
          const fetchedUser = mapPatientToIsUser(response.data);
          setIsUser(fetchedUser);
          logAnalyticsEvent(ANALYTICS_EVENTS.PATIENT_DETAILS_FETCHED, {
            method: PATIENT_DETAILS_FETCH_METHOD.AFTER_VERIFICATION,
            abha_verified: !!fetchedUser.isAbhaVerified,
          });
        }
        setPatientSource(
          isUser.isCreate ? COLLECTION_SOURCE.NEW_PATIENT : COLLECTION_SOURCE.VERIFIED
        );
        setStep(3);
      } else {
        toast.error(response.message);
        logAnalyticsEvent(ANALYTICS_EVENTS.OTP_VERIFICATION_FAILED, {
          patient_type: patientType,
        });
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      toast.error(error.message);
      logAnalyticsEvent(ANALYTICS_EVENTS.OTP_VERIFICATION_FAILED, {
        patient_type: patientType,
      });
    } finally {
      setVerifyOtpLoading(false);
    }
  };

  // send OTP
  const handleSendOtp = async () => {
    const patientType = isUser.isCreate ? PATIENT_TYPE.NEW : PATIENT_TYPE.EXISTING;
    if (isUser.isCreate && (isUser.phone || "").length !== phoneLimit) {
      toast.error(getPhoneNumberLimitMessage(isUser.countryCode || "+91"));
      return;
    }

    try {
      let response;
      if (isUser.isCreate) {
        response = await createPatient(getPatientPhonePayload());
      } else {
        setVerifyOtpLoading(true);
        response = await FetchPatient({ id: isUser.id, otp });
      }

      if (response.success) {
        toast.success(response.message);
        setSendOtp(true);
        logAnalyticsEvent(ANALYTICS_EVENTS.SEND_OTP_SUCCESS, {
          patient_type: patientType,
        });
        if (!isUser.isCreate && response.data) {
          setIsUser(mapPatientToIsUser(response.data));
        }
      } else {
        toast.error(response.message);
        logAnalyticsEvent(ANALYTICS_EVENTS.SEND_OTP_FAILED, {
          patient_type: patientType,
        });
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      toast.error(error.message);
      logAnalyticsEvent(ANALYTICS_EVENTS.SEND_OTP_FAILED, {
        patient_type: patientType,
      });
    } finally {
      setVerifyOtpLoading(false);
    }
  };


  // called from AbhaVerificationModal
  const updateAbhaUser = (userFromAbha) => {
    const mapped = mapPatientToIsUser(userFromAbha);
    // ensure flags
    mapped.isAbhaVerified = true;
    mapped.isVerifiedUser = false;
    setIsUser(mapped);
    setPatientSource(COLLECTION_SOURCE.VERIFIED);
    logAnalyticsEvent(ANALYTICS_EVENTS.VERIFY_PATIENT_THROUGH_ABHA_SUCCESS, {
      patient_type: isUser.isCreate ? PATIENT_TYPE.NEW : PATIENT_TYPE.EXISTING,
    });
    logAnalyticsEvent(ANALYTICS_EVENTS.PATIENT_DETAILS_FETCHED, {
      method: PATIENT_DETAILS_FETCH_METHOD.THROUGH_ABHA,
      abha_verified: true,
    });
    setStep(3);
    setShowAbha(false);
  };

  const renderStepContent = (currentStep) => {
    switch (currentStep) {
      case 0:
        return (
          <Box
            sx={{
              mt: 3,
              p: 3,
              borderRadius: "12px",
              backgroundColor: "var(--app-surface)",
              boxShadow: "none",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                mb: 1,
                fontWeight: 500,
                fontSize: "15px",
                color: "var(--app-text-muted)",
                fontFamily: "Figtree",
              }}
            >
              Selected Centre <span style={{ color: "red" }}>*</span>
            </Typography>

            <CentreSelect
              value={selectedCentre}
              onChange={handleCentreChange}
              placeholder="Select Centre"
              showSelectedValue
              disabled={false}
              autoSelectSingleCentre={true}
            />
          </Box>
        );

      case 1:
        return (
          <>
            <Card
              sx={{
                mt: 4,
                borderRadius: "14px",
                p: "8px",
                boxShadow: "none",
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{
                    color: "var(--app-text-muted)",
                    fontSize: "15px",
                    fontWeight: "500",
                    fontFamily: "Figtree",
                  }}
                >
                  Search Patient <span style={{ color: "red" }}>*</span>
                </Typography>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSearch(searchQuery);
                  }}
                >
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    sx={{
                      mt: 2,
                      flexWrap: { xs: "wrap", md: "nowrap" },
                    }}
                  >
                    {/* Search Input */}
                    <Grid
                      item
                      xs={12}
                      md
                      sx={{
                        width: { xs: "100%", md: "85%" },
                      }}
                    >
                      <TextField
                        fullWidth
                        placeholder="Phone Number, UHID, Reference Number"
                        value={searchQuery}
                        error={searchError}
                        helperText={
                          searchError ? "This field is required" : " "
                        }
                        onChange={(e) => {
                          let value = e.target.value;

                          // Check if value is only numbers
                          if (/^\d+$/.test(value)) {
                            value = value.slice(0, 10); // max 10 digits
                          } else {
                            value = value.slice(0, 20); // max 20 chars for string
                          }

                          setSearchQuery(value);
                          setSearchError(false);
                          setHasSearched(false);
                        }}
                        InputProps={{
                          inputMode: "numeric",
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: "var(--app-text-muted)" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          backgroundColor: "var(--app-surface)",
                          borderRadius: "12px",

                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            height: "48px",

                            "& fieldset": {
                              borderColor: "var(--app-border)",
                            },

                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "var(--app-border)",
                            },

                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "var(--app-border)",
                              borderWidth: "1px",
                            },
                          },

                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "var(--app-border)",
                          },

                          "& .MuiOutlinedInput-input": {
                            outline: "none !important",
                          },
                        }}
                      />
                    </Grid>

                    {/* Search Button */}
                    <Grid
                      item
                      xs={12}
                      md="auto"
                      sx={{
                        width: { xs: "100%", md: "15%" },
                      }}
                    >
                      <LoadingButton
                        fullWidth
                        type="submit"
                        variant="contained"
                        color="primary"
                        loading={verifyOtpLoading}
                        sx={{
                          textTransform: "none",
                          py: 1.2,
                          backgroundColor: "#5751E9",

                          height: "48px",
                          borderRadius: "9px",
                          fontSize: "15px",
                          fontWeight: "600",
                          fontFamily: "Figtree",
                          boxShadow: "none",
                          marginTop: searchError ? "-20px" : "-20px",

                          "&:hover": {
                            backgroundColor: "#5751E9",
                            boxShadow: "none",
                          },
                          "&:active": {
                            boxShadow: "none",
                          },
                          "&.Mui-focusVisible": {
                            boxShadow: "none",
                          },
                        }}
                      >
                        Search
                      </LoadingButton>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>

            {/* Results */}
            {hasSearched && (
              <Card
                sx={{
                  mt: 3,
                  p: 2,
                  boxShadow: "none",
                  borderRadius: "14px",
                }}
              >
                {filteredData.length > 0 ? (
                  filteredData.map((option) => (
                    <Box
                      key={option._id}
                      onClick={() => handleSelectUser(option)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pt: 1.5,
                        pb: 1.5,
                        borderBottom: "1px solid var(--app-border)",
                        cursor: "pointer",
                      }}
                    >
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                            fontWeight: 600,
                            fontSize: 16,
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, color: "var(--app-text)" }}
                          >
                            {option?.firstName} {option?.lastName}
                          </Typography>

                          {option?.isVerifiedUser && (
                            <Chip
                              icon={<i className="fa fa-check-circle" />}
                              label="Verified User"
                              sx={{
                                color: "#5751E9",
                                backgroundColor: "var(--app-unread)",
                                border: "0.4px solid #F3F5FF",
                                fontSize: 12,
                                fontWeight: 600,
                                borderRadius: "5px",
                                "& .MuiChip-label": { px: 1 },
                                "& .MuiChip-icon": { color: "#5751E9" },
                              }}
                            />
                          )}

                          {option?.isAbhaVerified && (
                            <Chip
                              icon={<i className="fa fa-check-circle" />}
                              label="ABHA Verified"
                              sx={{
                                color: "#348530",
                                backgroundColor: "#DAF4E180",
                                fontSize: 12,
                                fontWeight: 600,
                                borderRadius: "5px",
                                "& .MuiChip-label": { px: 1 },
                                "& .MuiChip-icon": { color: "#348530" },
                                border: "0.4px solid #F3F5FF",
                              }}
                            />
                          )}
                        </Box>

                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: "var(--app-text)",
                          }}
                        >
                          XXXXXX{option?.phoneNumber?.slice(-4)}
                        </Typography>
                      </Box>

                      <Typography
                        sx={{
                          fontSize: 15,
                          fontWeight: 500,
                          color: "var(--app-text)",
                        }}
                      >
                        {option?.age} yrs | {option?.gender}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Figtree",
                        fontSize: "15px",
                        color: "var(--app-text)",
                        fontWeight: "500",
                      }}
                    >
                      No patients found matching your search criteria.
                    </Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    alignItems: "center",
                    color: "#5A48FA",
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                  onClick={() => handleSelectUser(null)}
                >
                  <i className="fa fa-plus" aria-hidden="true"></i>
                  &nbsp; Create New Patient
                </Box>
              </Card>
            )}
          </>
        );

      case 2:
        return (
          <Box className="card mt-3" sx={{ p: { xs: 2, md: 3 } }}>
            {!sendotp ? (
              <>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, fontSize: "19px", color: "var(--app-text)" }}
                >
                  Verify Patient
                </Typography>

                {/* <Typography sx={{ mt: 1 ,fontWeight: 400, fontSize: "14px", color:"var(--app-text)"}}>
                  A one-time password will be sent to the patient’s phone number
                  and this number will be linked to Patient’s Spandan Records
                </Typography> */}

                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{
                      mb: 1,
                      fontWeight: 500,
                      fontSize: "15px",
                      color: "var(--app-text-muted)",
                    }}
                  >
                    Contact Number <span style={{ color: "red" }}>*</span>
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      gap: 2,
                      alignItems: { md: "center" },
                    }}
                  >
                    {/* Country Code */}
                    <Box sx={{ width: { xs: "100%", md: "83px" }, height: 48 }}>
                      <CountryCodePicker
                        value={isUser.countryCode}
                        disabled={isUser.id !== 0}
                        onChange={(nextCountryCode) =>
                          setIsUser((prev) => ({
                            ...prev,
                            countryCode: nextCountryCode,
                            phone: sanitizePhoneNumber(prev.phone, nextCountryCode),
                          }))
                        }
                      />
                    </Box>

                    {/* Phone + Button */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: { md: "center" },
                        gap: 2,
                        flex: 1,
                      }}
                    >
                      <Box
                        component="form"
                        onSubmit={(e) => {
                          e.preventDefault();
                          logAnalyticsEvent(ANALYTICS_EVENTS.CREATE_NEW_PROFILE, {
                            method: CREATE_PROFILE_METHOD.PHONE_NUMBER,
                          });
                          logAnalyticsEvent(ANALYTICS_EVENTS.SEND_OTP_CLICKED, {
                            patient_type: isUser.isCreate
                              ? PATIENT_TYPE.NEW
                              : PATIENT_TYPE.EXISTING,
                          });
                          handleSendOtp(isUser.phone);
                        }}
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", md: "row" },
                          gap: 2,
                          height: { xs: "auto", md: 48 },
                          width: { xs: "100%", md: "436px" },
                          alignItems: { md: "center" },
                        }}
                      >
                        {/* Input */}
                        <Box sx={{ flex: 1 }}>
                          <input
                            type="text"
                            onChange={(e) =>
                              setIsUser((prev) => ({
                                ...prev,
                                phone: sanitizePhoneNumber(
                                  e.target.value,
                                  prev.countryCode || "+91"
                                ),
                              }))
                            }
                            className="form-control"
                            placeholder="9876543210"
                            readOnly={isUser.id !== 0}
                            maxLength={phoneLimit}
                            inputMode="numeric"
                            value={
                              isUser.id !== 0
                                ? "XXXXXX" + (isUser?.phone || "").slice(-4)
                                : isUser?.phone || ""
                            }
                            style={
                              isUser.id !== 0
                                ? { backgroundColor: "var(--app-surface-2)", height: 48 }
                                : { height: 48 }
                            }
                          />
                        </Box>

                        {/* Button */}
                        <Box sx={{ minWidth: { xs: "100%", md: 140 } }}>
                          {verifyOtpLoading ? (
                            <Button
                              type="submit"
                              variant="contained"
                              disabled
                              fullWidth
                              sx={{
                                textTransform: "none",
                                height: 48,
                                backgroundColor: "#5751E9",
                              }}
                            >
                              Sending OTP
                            </Button>
                          ) : (
                            <Button
                              type="submit"
                              variant="contained"
                              disabled={
                                !isUser?.phone ||
                                (isUser.id === 0 && isUser.phone.length !== phoneLimit)
                              }
                              sx={{
                                textTransform: "none",
                                height: 48,
                                borderRadius: "9px",
                                backgroundColor: "#5751E9",
                                width: { xs: "100%", md: "129px" },
                                "&.Mui-disabled": {
                                  backgroundColor: "var(--app-border)", // disabled bg
                                  color: "var(--app-text-muted)", // disabled text color
                                },
                              }}
                            >
                              Send OTP
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {selectedCentre?.isAbhaVerified && (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          my: 4,
                          gap: 2,
                          width: "100%",
                          maxWidth: { md: "673px" },
                        }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            border: "1px solid var(--app-border)",
                            width: { xs: "100%", md: "284px" },
                          }}
                        />

                        <Typography
                          sx={{
                            fontWeight: 500,
                            color: "var(--app-text-muted)",
                            fontSize: "14px",
                            whiteSpace: "nowrap",
                            fontFamily: "Figtree",
                          }}
                        >
                          OR
                        </Typography>

                        <Box
                          sx={{
                            flex: 1,
                            border: "1px solid var(--app-border)",
                            width: { xs: "100%", md: "284px" },
                          }}
                        />
                      </Box>

                      <Box
                        onClick={() => {
                          logAnalyticsEvent(ANALYTICS_EVENTS.CREATE_NEW_PROFILE, {
                            method: CREATE_PROFILE_METHOD.ABHA,
                          });
                          logAnalyticsEvent(
                            ANALYTICS_EVENTS.VERIFY_PATIENT_THROUGH_ABHA,
                            {
                              patient_type: isUser.isCreate
                                ? PATIENT_TYPE.NEW
                                : PATIENT_TYPE.EXISTING,
                            }
                          );
                          setShowAbha(true);
                        }}
                        sx={{
                          cursor: "pointer",
                          border: "1px solid #5751E933",
                          borderRadius: "12px",
                          p: 2,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexDirection: "row", //  always row
                          gap: 1,
                          backgroundColor: "var(--app-unread)",
                          width: { xs: "100%", md: "529px" },
                        }}
                      >
                        <Typography
                          fontWeight={600}
                          fontSize={16}
                          sx={{
                            color: "#5751E9",
                            flex: 1, // take remaining space
                            pr: 1,
                          }}
                        >
                          Create New Profile Using ABHA Account
                        </Typography>

                        <i
                          className="fa fa-chevron-right"
                          aria-hidden="true"
                          style={{ fontSize: 16, flexShrink: 0 }} //  prevent icon wrap
                        ></i>
                      </Box>
                    </>
                  )}
                </Box>
              </>
            ) : (
              <>
                <Typography variant="h6" fontWeight={700}>
                  Verify Patient
                </Typography>

                <Typography sx={{ mt: 1 }}>
                  A one-time password will be sent to the patient’s phone number
                  and this number will be linked to Patient’s Spandan Records
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <OtpInput
                    value={otp}
                    onChange={(otpVal) => {
                      setOtp(otpVal);
                      if (otpVal.length === 6) {
                        handleOtpVerification(otpVal);
                      }
                    }}
                    renderInput={(props) => (
                      <input
                        {...props}
                        placeholder="#"
                        readOnly={verifyOtpLoading}
                      />
                    )}
                    numInputs={6}
                    renderSeparator={<span> </span>}
                    shouldAutoFocus={true}
                    inputStyle={{
                      width: "50px",
                      height: "50px",
                      marginRight: "0.6rem",
                      borderRadius: "8px",
                      border: "1px solid var(--app-border)",
                    }}
                  />

                  <Box sx={{ mt: 3 }}>
                    <Timer
                      seconds={30}
                      minutes={0}
                      resend={() => {
                        logAnalyticsEvent(ANALYTICS_EVENTS.RESEND_OTP_CLICKED, {
                          patient_type: isUser.isCreate
                            ? PATIENT_TYPE.NEW
                            : PATIENT_TYPE.EXISTING,
                        });
                        handleSendOtp();
                      }}
                      text={"Didn't get the code?, Resend"}
                      buttonColor={"var(--app-text)"}
                      background="var(--app-surface)"
                      ButtonText={"Didn't get the code? Resend OTP"}
                    />
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    {verifyOtpLoading ? (
                      <Button
                        variant="contained"
                        fullWidth
                        sx={{
                          textTransform: "none",
                          height: 48,
                          backgroundColor: "#5751E9",
                        }}
                      >
                        Verifying OTP
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => {
                          logAnalyticsEvent(
                            ANALYTICS_EVENTS.VERIFY_OTP_BUTTON_CLICKED,
                            {
                              patient_type: isUser.isCreate
                                ? PATIENT_TYPE.NEW
                                : PATIENT_TYPE.EXISTING,
                            }
                          );
                          handleOtpVerification(otp);
                        }}
                        sx={{
                          textTransform: "none",
                          height: 48,
                          width: { xs: "100%", md: "129px" },
                          backgroundColor: "#5751E9",
                        }}
                      >
                        Verify OTP
                      </Button>
                    )}
                  </Box>
                </Box>
              </>
            )}
          </Box>
        );

      case 3:
        return (
          <UserFlow
            isUser={isUser}
            selectedCentre={selectedCentre}
            setIsUser={setIsUser}
            source={patientSource}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Spandan - Create Collection Request</title>
      </Helmet>

      <div className="main-wrappers">
        <div
          className="page-wrapper"
          style={{
            paddingBottom: "10px",
            paddingTop: "75px",
            backgroundColor: "var(--app-bg)",
            minHeight: "100vh",
          }}
        >
          <div className="content">
            <Breadcrumbs separator="/" sx={{ mb: 1 }}>
              <Link
                component={RouterLink}
                to="/test-collections"
                underline="hover"
                sx={{
                  fontSize: "14px",
                  fontFamily: "Figtree",
                  color: "var(--app-text)",
                  fontWeight: 400,
                  fontStyle: "regular",
                }}
              >
                Test Collections
              </Link>

              <Typography
                color="var(--app-text)"
                sx={{
                  fontSize: "14px",
                  fontFamily: "Figtree",
                  fontWeight: 600,
                }}
              >
                New Test Collection
              </Typography>
            </Breadcrumbs>

            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5
                onClick={() => {
                  navigate(-1);
                }}
                style={{ cursor: "pointer" }}
              >
                <strong className="d-flex align-items-center">
                  New Test Collection
                </strong>
              </h5>

              {step === 1 && (
                <Box
                  sx={{
                    width: "200px",
                    p: "8px 12px",
                    border: "1px solid var(--app-border)",
                    borderRadius: "8px",
                    backgroundColor: "var(--app-surface-2)",
                    color: "var(--app-text-muted)",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontFamily: "Figtree",
                    cursor: "pointer",
                    pointerEvents: "none",
                    userSelect: "none",
                  }}
                >
                  {selectedCentre?.label || "No Centre Selected"}
                </Box>
              )}
            </div>

            {renderStepContent(step)}
          </div>
        </div>
      </div>

      {/* ABHA modal */}
      <Dialog
        open={showAbha}
        fullWidth
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setShowAbha(false);
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            padding: 0,
          },
        }}
      >
        <AbhaVerificationModal
          onClose={() => {
            setShowAbha(false);
          }}
          updateAbhaUser={updateAbhaUser}
        />
      </Dialog>
    </>
  );
}

export default CreateCollectionTasks;
